import { textColorFields } from '../_shared/textColor.js';
import { universalFields } from '../_shared/universal.js';
import { headingHighlightFields } from '../_shared/headingHighlight.js';
/**
 * trustpilot-reviews — Shrine "Trustpilot reviews" at CONFIG-BY-CONFIG parity. The distinctive Trustpilot
 * layout: a heading (with the shared word-HIGHLIGHT engine) + a "Excellent 4.8 / 5 ★★★★★" SUBHEADING (the
 * Trustpilot 5-square star bar, clipped to a rating) + optional text, over a responsive grid/slider of
 * `.trustpilot-review` cards. Each REVIEW is an inline child block: its own Trustpilot star bar + colours,
 * a heading, a body, and an author (+ avatar). It is a sibling of `testimonials` (shares the slider engine,
 * the heading-highlight + pattern engines, and the `testimonial-card__author-container` author row), but
 * with Trustpilot's block-star SVG instead of Font-Awesome glyphs, plus the content-layout (content above /
 * left / right of the reviews) and the rating subheading.
 *
 * SHRINE PARITY: every General / Content-layout / Heading / Subheading / Text / Review-cards / Slider /
 * Desktop / Mobile knob is implemented here and wired in Component.tsx. The Shopify-platform-only knobs are
 * DEFERRED with justification (see the bottom of this file + Component.tsx): `display_id` (section-merge UI
 * helper), the five section/cards `color_scheme` selects map onto our `colorScheme`/slider-scheme tokens,
 * the `custom_image_background` pair (Shopify img_url pipeline), the live Splide carousel JS (autoplay/loop
 * need a client island — the controls + native scroll-snap render), and the `line-1/2/3` heading decorations
 * use CSS text-decoration rather than Shrine's SVG glyph assets.
 */
export const trustpilotReviewsSchema = {
    type: 'trustpilot-reviews',
    name: 'Trustpilot reviews',
    version: 1,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    fields: [
        // ── General (Shrine HEADER: General) ─────────────────────────────────────
        /**
         * Feature 0066 — this section imitates a review wall; here it can stop imitating and become one.
         * Default MANUAL: an installed app never rewrites a page the seller already published.
         */
        {
            id: 'source',
            type: 'select',
            label: 'Where the cards come from',
            group: 'General',
            default: 'manual',
            options: [
                { value: 'manual', label: 'The cards I write' },
                { value: 'app', label: 'My real reviews' },
            ],
            info: 'With “My real reviews”, the cards are the published reviews of this product. The section hides itself when there are none.',
        },
        {
            id: 'reviewsToShow',
            type: 'range',
            label: 'Reviews to show',
            group: 'General',
            default: 6,
            min: 1,
            max: 24,
            step: 1,
            visibleWhen: { field: 'source', equals: 'app' },
        },
        {
            id: 'visibility',
            type: 'select',
            label: 'Visibility',
            group: 'General',
            default: 'always-display',
            options: [
                { value: 'always-display', label: 'Always display' },
                { value: 'desktop-hidden', label: 'Hide on desktop' },
                { value: 'mobile-hidden', label: 'Hide on mobile' },
            ],
        },
        // ── Background pattern (shared engine — Shrine pattern_bg cluster). ────────
        {
            id: 'patternBg',
            type: 'select',
            label: 'Background pattern',
            group: 'General',
            default: 'none',
            options: [
                { value: 'none', label: 'None' },
                { value: 'dotted', label: 'Dots' },
                { value: 'grid', label: 'Grid' },
                { value: 'curved-grid', label: 'Curved grid' },
                { value: 'diagonal-stripes', label: 'Stripes' },
                { value: 'zigzag', label: 'ZigZag' },
                { value: 'arrow-pattern', label: '3D Boxes' },
                { value: 'radial-rings', label: 'Rings' },
                { value: 'diamond-grid-1', label: 'Diamond grid 1' },
                { value: 'diamond-grid-2', label: 'Diamond grid 2' },
                { value: 'quatrefoils', label: 'Quatrefoils' },
                { value: 'ovals', label: 'Ovals' },
                { value: 'hexagons', label: 'Hexagons' },
                { value: 'curvy', label: 'Curvy' },
            ],
        },
        {
            id: 'patternColor',
            type: 'color',
            label: 'Pattern color',
            group: 'General',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternSize',
            type: 'range',
            label: 'Pattern size / spacing',
            group: 'General',
            min: 4,
            max: 80,
            step: 2,
            unit: 'px',
            default: 20,
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternThickness',
            type: 'range',
            label: 'Line / dot thickness',
            group: 'General',
            min: 1,
            max: 10,
            step: 1,
            unit: 'px',
            default: 2,
            visibleWhen: {
                field: 'patternBg',
                equals: ['dotted', 'grid', 'diagonal-stripes', 'radial-rings'],
            },
        },
        {
            id: 'patternAngle',
            type: 'range',
            label: 'Stripe angle',
            group: 'General',
            min: 0,
            max: 180,
            step: 15,
            unit: 'deg',
            default: 45,
            visibleWhen: { field: 'patternBg', equals: 'diagonal-stripes' },
        },
        {
            id: 'patternOpacityMode',
            type: 'select',
            label: 'Opacity type',
            group: 'General',
            default: 'low',
            options: [
                { value: 'low', label: 'Low' },
                { value: 'normal', label: 'Normal' },
            ],
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternOpacityLow',
            type: 'range',
            label: 'Opacity',
            group: 'General',
            min: 1,
            max: 20,
            step: 1,
            unit: '%',
            default: 7,
            visibleWhen: { field: 'patternOpacityMode', equals: 'low' },
        },
        {
            id: 'patternOpacityNormal',
            type: 'range',
            label: 'Opacity',
            group: 'General',
            min: 20,
            max: 100,
            step: 5,
            unit: '%',
            default: 80,
            visibleWhen: { field: 'patternOpacityMode', equals: 'normal' },
        },
        // ── Content layout (Shrine HEADER: Content layout) ───────────────────────
        {
            id: 'desktopContentPosition',
            type: 'select',
            label: 'Desktop content position',
            group: 'Content layout',
            info: 'Applies to the heading, subheading and text.',
            default: 'above',
            options: [
                { value: 'above', label: 'Above reviews' },
                { value: 'left', label: 'Left side of the reviews' },
                { value: 'right', label: 'Right side of the reviews' },
            ],
        },
        {
            id: 'desktopContentAlignment',
            type: 'select',
            label: 'Desktop content alignment',
            group: 'Content layout',
            default: 'center',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
        },
        // ── Heading (Shrine HEADER: Heading) ─────────────────────────────────────
        {
            id: 'displaySectionId',
            type: 'checkbox',
            label: 'Display section ID',
            group: 'General',
            info: 'Adds an anchor id to this section so a link can scroll to it.',
            default: false,
        },
        {
            id: 'heading',
            type: 'text',
            label: 'Heading',
            group: 'Heading',
            default: 'Trustpilot reviews',
            defaultKey: 'section.trustpilot_reviews',
            maxLength: 120,
            info: 'Wrap words in ** ** (highlight 1) or * * (highlight 2) to decorate them.',
        },
        ...headingHighlightFields(1, 'Heading'),
        ...headingHighlightFields(2, 'Heading'),
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: 'Heading',
            default: 'h1',
            options: [
                { value: 'h3', label: 'Extra small' },
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'customDesktopSize',
            type: 'range',
            label: 'Custom desktop size',
            group: 'Heading',
            min: 24,
            max: 60,
            step: 3,
            unit: 'px',
            default: 39,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'customMobileSize',
            type: 'range',
            label: 'Custom mobile size',
            group: 'Heading',
            min: 20,
            max: 50,
            step: 2,
            unit: 'px',
            default: 28,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        // ── Subheading (Shrine HEADER: Subheading — the rating line) ─────────────
        {
            id: 'subtitle',
            type: 'text',
            label: 'Subheading',
            group: 'Subheading',
            default: 'Excellent [rating] <strong>/ 5</strong> [rating_stars]',
            defaultKey: 'section.trustpilot_subtitle',
            maxLength: 200,
            info: 'Use [rating] for the highlighted "Rating text" and [rating_stars] for the star bar.',
        },
        {
            id: 'subheadingRatingText',
            type: 'text',
            label: 'Rating text',
            group: 'Subheading',
            default: '<strong>4.8</strong>',
            maxLength: 40,
            info: 'Replaces [rating] in the subheading.',
        },
        {
            id: 'subheadingMobileTextSize',
            type: 'range',
            label: 'Mobile subheading text size',
            group: 'Subheading',
            min: 12,
            max: 28,
            step: 1,
            unit: 'px',
            default: 18,
        },
        {
            id: 'subheadingDesktopTextSize',
            type: 'range',
            label: 'Desktop subheading text size',
            group: 'Subheading',
            min: 14,
            max: 36,
            step: 1,
            unit: 'px',
            default: 20,
        },
        {
            id: 'subheadingFont',
            type: 'select',
            label: 'Subheading font family',
            group: 'Subheading',
            default: 'body',
            options: [
                { value: 'headings', label: 'Headings' },
                { value: 'body', label: 'Body' },
            ],
        },
        {
            id: 'starColor',
            type: 'color',
            label: 'Active stars container color',
            group: 'Subheading',
            default: { ref: 'custom', value: '#00b67a' },
        },
        {
            id: 'bgStarColor',
            type: 'color',
            label: 'Background stars container color',
            group: 'Subheading',
            default: { ref: 'custom', value: '#c8c8c8' },
        },
        {
            id: 'starSymbolColor',
            type: 'color',
            label: 'Stars inside symbol color',
            group: 'Subheading',
            default: { ref: 'custom', value: '#ffffff' },
        },
        {
            id: 'starsRating',
            type: 'range',
            label: 'Stars rating',
            group: 'Subheading',
            min: 1,
            max: 5,
            step: 0.5,
            default: 5,
            info: 'Rating of the stars displayed in the subheading.',
        },
        // ── Text (Shrine HEADER: Text) ───────────────────────────────────────────
        {
            id: 'body',
            type: 'richtext',
            label: 'Text',
            group: 'Text',
        },
        // ── Review cards (Shrine HEADER: Review cards) ───────────────────────────
        {
            id: 'cardsBorderRadius',
            type: 'range',
            label: 'Corner radius',
            group: 'Review cards',
            min: 0,
            max: 40,
            step: 2,
            unit: 'px',
            default: 16,
        },
        {
            id: 'cardAlignment',
            type: 'select',
            label: 'Text alignment',
            group: 'Review cards',
            default: 'center',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
        },
        {
            id: 'showStars',
            type: 'checkbox',
            label: 'Show stars',
            group: 'Review cards',
            default: true,
            info: 'Star rating & colour are set per review (block settings).',
        },
        {
            id: 'cardsScheme',
            type: 'select',
            label: 'Cards color scheme',
            group: 'Review cards',
            default: 'background-2',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Inverse' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'cardsCustomBackground',
            type: 'color',
            label: 'Cards background',
            group: 'Review cards',
            default: { ref: 'custom', value: '#F3F3F3' },
            visibleWhen: { field: 'cardsScheme', equals: 'custom' },
        },
        {
            id: 'cardsCustomGradient',
            type: 'gradient',
            label: 'Cards gradient',
            group: 'Review cards',
            visibleWhen: { field: 'cardsScheme', equals: 'custom' },
        },
        {
            id: 'cardsCustomText',
            type: 'color',
            label: 'Cards text',
            group: 'Review cards',
            default: { ref: 'custom', value: '#2E2A39' },
            visibleWhen: { field: 'cardsScheme', equals: 'custom' },
        },
        // ── Slider (Shrine HEADER: Slider) ───────────────────────────────────────
        {
            id: 'sliderType',
            type: 'select',
            label: 'Slider type',
            group: 'Slider',
            default: 'slide',
            info: 'Classic (Slide) or infinite (Loop). The looping/autoplay carousel needs a client island.',
            options: [
                { value: 'slide', label: 'Classic' },
                { value: 'loop', label: 'Infinite loop' },
            ],
        },
        {
            id: 'autoplay',
            type: 'checkbox',
            label: 'Autoplay',
            group: 'Slider',
            default: false,
        },
        {
            id: 'autoplaySpeed',
            type: 'range',
            label: 'Autoplay speed',
            group: 'Slider',
            min: 1,
            max: 15,
            step: 0.5,
            unit: 's',
            default: 5,
            visibleWhen: { field: 'autoplay', equals: true },
        },
        {
            id: 'transparentArrows',
            type: 'checkbox',
            label: 'Transparent arrows',
            group: 'Slider',
            default: true,
        },
        {
            id: 'arrowsScheme',
            type: 'select',
            label: 'Arrows color',
            group: 'Slider',
            default: 'inverse',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Inverse' },
            ],
        },
        {
            id: 'dotsScheme',
            type: 'select',
            label: 'Dots color',
            group: 'Slider',
            default: 'inverse',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Inverse' },
            ],
        },
        // ── Desktop (Shrine HEADER: Desktop) ─────────────────────────────────────
        {
            id: 'desktopFullPage',
            type: 'checkbox',
            label: 'Full page width',
            group: 'Desktop',
            default: false,
        },
        {
            id: 'columnsDesktop',
            type: 'range',
            label: 'Slides per page',
            group: 'Desktop',
            min: 1,
            max: 10,
            step: 1,
            default: 3,
        },
        {
            id: 'sliderDesktop',
            type: 'checkbox',
            label: 'Enable desktop slider',
            group: 'Desktop',
            default: false,
            info: 'When off, cards lay out as a static grid on desktop.',
        },
        {
            id: 'perMoveDesktop',
            type: 'range',
            label: 'Slides to scroll per move',
            group: 'Desktop',
            min: 1,
            max: 10,
            step: 1,
            default: 1,
            visibleWhen: { field: 'sliderDesktop', equals: true },
        },
        {
            id: 'desktopSpacing',
            type: 'range',
            label: 'Spacing',
            group: 'Desktop',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 24,
        },
        {
            id: 'desktopSidePadding',
            type: 'range',
            label: 'Preview of prev & next slide',
            group: 'Desktop',
            min: 0,
            max: 150,
            step: 5,
            unit: 'px',
            default: 0,
            visibleWhen: { field: 'sliderDesktop', equals: true },
        },
        {
            id: 'desktopPaddingCalc',
            type: 'checkbox',
            label: 'Disable empty preview on first & last slide',
            group: 'Desktop',
            default: true,
            visibleWhen: { field: 'sliderDesktop', equals: true },
        },
        {
            id: 'desktopAdaptiveHeight',
            type: 'checkbox',
            label: 'Adaptive height',
            group: 'Desktop',
            default: false,
            info: 'Only applies when Slides per page is 1.',
            visibleWhen: { field: 'sliderDesktop', equals: true },
        },
        {
            id: 'desktopDots',
            type: 'select',
            label: 'Dots position (desktop)',
            group: 'Desktop',
            default: 'under',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
            ],
        },
        {
            id: 'desktopArrows',
            type: 'select',
            label: 'Arrows position (desktop)',
            group: 'Desktop',
            default: 'sides',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
                { value: 'sides', label: 'On sides of the slider' },
            ],
        },
        // ── Mobile (Shrine HEADER: Mobile) ───────────────────────────────────────
        {
            id: 'sliderMobile',
            type: 'checkbox',
            label: 'Enable mobile slider',
            group: 'Mobile',
            default: true,
        },
        {
            id: 'mobilePreview',
            type: 'checkbox',
            label: 'Preview of prev & next slides',
            group: 'Mobile',
            default: false,
            visibleWhen: { field: 'sliderMobile', equals: true },
        },
        {
            id: 'mobileAdaptiveHeight',
            type: 'checkbox',
            label: 'Adaptive height',
            group: 'Mobile',
            default: false,
            visibleWhen: { field: 'sliderMobile', equals: true },
        },
        {
            id: 'mobileDots',
            type: 'select',
            label: 'Dots position (mobile)',
            group: 'Mobile',
            default: 'under',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
            ],
        },
        {
            id: 'mobileArrows',
            type: 'select',
            label: 'Arrows position (mobile)',
            group: 'Mobile',
            default: 'under',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
                { value: 'sides', label: 'On sides of the slider' },
            ],
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // ── Section (universal: color scheme, padding_top/bottom, width) ─────────
        ...universalFields,
    ],
    // The REVIEW inline block (Shrine `column`): per-card Trustpilot stars + colours + heading + body + author.
    blocks: [
        {
            type: 'review',
            name: 'Review',
            max: 12,
            fields: [
                {
                    id: 'starsRating',
                    type: 'range',
                    label: 'Stars rating',
                    min: 1,
                    max: 5,
                    step: 0.5,
                    default: 5,
                },
                {
                    id: 'starColor',
                    type: 'color',
                    label: 'Active stars container color',
                    default: { ref: 'custom', value: '#00b67a' },
                },
                {
                    id: 'bgStarColor',
                    type: 'color',
                    label: 'Background stars container color',
                    default: { ref: 'custom', value: '#c8c8c8' },
                },
                {
                    id: 'starSymbolColor',
                    type: 'color',
                    label: 'Stars inside symbol color',
                    default: { ref: 'custom', value: '#ffffff' },
                },
                { id: 'title', type: 'text', label: 'Heading', default: 'Heading', maxLength: 120 },
                {
                    id: 'text',
                    type: 'richtext',
                    label: 'Text',
                    default: {
                        format: 'html',
                        html: '<p>Share positive thoughts and feedback from your customer..</p>',
                    },
                },
                { id: 'authorAvatar', type: 'image', label: 'Author avatar' },
                { id: 'author', type: 'text', label: 'Author', default: 'Author', maxLength: 80 },
            ],
        },
    ],
    maxBlocks: 12,
};
export const trustpilotReviewsPresets = [
    {
        name: 'Trustpilot reviews',
        settings: {
            visibility: 'always-display',
            desktopContentPosition: 'above',
            desktopContentAlignment: 'center',
            headingSize: 'h1',
            headingHighlight1: 'solid-color',
            headingHighlight1Color: { ref: 'theme', token: 'accent' },
            subheadingRatingText: '<strong>4.8</strong>',
            subheadingMobileTextSize: 18,
            subheadingDesktopTextSize: 20,
            subheadingFont: 'body',
            starColor: { ref: 'custom', value: '#00b67a' },
            bgStarColor: { ref: 'custom', value: '#c8c8c8' },
            starSymbolColor: { ref: 'custom', value: '#ffffff' },
            starsRating: 5,
            cardsBorderRadius: 16,
            cardAlignment: 'center',
            showStars: true,
            cardsScheme: 'background-2',
            sliderType: 'slide',
            autoplay: false,
            transparentArrows: true,
            arrowsScheme: 'inverse',
            dotsScheme: 'inverse',
            desktopFullPage: false,
            columnsDesktop: 3,
            sliderDesktop: false,
            desktopSpacing: 24,
            desktopArrows: 'sides',
            desktopDots: 'under',
            sliderMobile: true,
            mobileArrows: 'under',
            mobileDots: 'under',
            // A clean LIGHT reviews band near the bottom of the page (matches Shrine's 36px default padding).
            colorScheme: 'light',
            paddingTop: 36,
            paddingBottom: 36,
            width: 'wide',
        },
        // Three default reviews (Shrine's preset ships 3 columns of default content).
        blocks: [
            {
                type: 'review',
                settings: {
                    starsRating: 5,
                    title: '@t:section.review_title_1',
                    text: {
                        format: 'html',
                        html: '<p>Commande reçue rapidement et produit conforme. Je recommande à 100% !</p>',
                    },
                    author: 'Sara B.',
                },
            },
            {
                type: 'review',
                settings: {
                    starsRating: 5,
                    title: '@t:section.review_title_2',
                    text: {
                        format: 'html',
                        html: '<p>Le service client est réactif et le paiement à la livraison m’a rassurée.</p>',
                    },
                    author: 'Yassine M.',
                },
            },
            {
                type: 'review',
                settings: {
                    starsRating: 5,
                    title: '@t:section.review_title_3',
                    text: {
                        format: 'html',
                        html: '<p>Qualité au rendez-vous, exactement comme sur les photos. Je reviendrai !</p>',
                    },
                    author: 'Imane K.',
                },
            },
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const trustpilotReviewsMeta = {
    label: 'Trustpilot reviews',
    group: 'Content',
    icon: 'star',
};
//# sourceMappingURL=schema.js.map